// Projected stock (N6), in the item's Plan section: today's available at HHL, then each PO or build landing, B2B order
// due, parts the CMs' builds take and units held for pre-sales on its day, and the forecast's sales between them, with
// the running balance against safety stock (amber below it, red below zero), projected the plan's way
// (shared/planning.ts stockTimeline()), and the headline read off the same days. In the usual table (views, columns,
// Copy / CSV), as tall as its rows up to 12, then it scrolls inside.
// Available-to-promise is a later step.
import { useMemo, useState } from "react";
import { addPlanDays, fmt, type ItemPlanResult, shortDate, stockTimeline, type TimelineEntry } from "../../../shared/planning.ts";
import { Badge, cx } from "../ui/controls.tsx";
import { type Column, DataTable } from "../ui/DataTable.tsx";
import { PaneLink } from "../ui/SidePane.tsx";
import { poPath } from "./po/lib.ts";

const KIND: Record<TimelineEntry["kind"], string> = {
  start: "",
  po: "PO",
  build: "Build",
  b2b: "B2B",
  parts: "Builds",
  reserved: "Held",
  sales: "Sales",
};

type Row = TimelineEntry & { id: string; order: number };

/** "+40", "−12", "0" (nothing for today's starting row). */
const change = (e: TimelineEntry) => (e.kind === "start" ? "" : Math.round(e.qty) === 0 ? "0" : `${e.qty > 0 ? "+" : "−"}${fmt(Math.round(Math.abs(e.qty)))}`);

export function StockTimeline({ r, today }: { r: ItemPlanResult; today: string }) {
  const { entries, floor, stockoutDay: out, belowDay: below } = useMemo(() => stockTimeline(r, today), [r, today]);
  const rows = useMemo<Row[]>(() => entries.map((e, order) => ({ ...e, id: e.key, order })), [entries]);
  // The headline: when it runs out, else when it falls below its floor (now when it's under already), off the same days.
  const day = (d: number) => (d <= 0 ? "now" : shortDate(addPlanDays(today, d), today));
  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: "when",
        header: "When",
        width: "76px",
        value: (e) => e.order, // the projection's own order: a day's landings, then what it takes, then its sales
        text: (e) => (e.kind === "start" ? "Today" : e.date),
        cell: (e) => <span className={cx(e.late ? "text-danger" : "text-muted")}>{e.kind === "start" ? "Today" : shortDate(e.date, today)}</span>,
      },
      {
        key: "kind",
        header: "Kind",
        width: "70px",
        value: (e) => KIND[e.kind],
        cell: (e) => KIND[e.kind] && <Badge tone={e.qty > 0 ? "ok" : "neutral"}>{KIND[e.kind]}</Badge>,
      },
      {
        key: "what",
        header: "What",
        width: "minmax(140px,1fr)",
        value: (e) => e.label,
        cell: (e) => (
          <span className={cx("truncate", e.kind === "sales" && "text-muted")}>
            {e.poId ? (
              <PaneLink kind="po" id={e.poId} to={poPath(e.poId)} className="font-mono hover:text-accent-text hover:underline">
                {e.label}
              </PaneLink>
            ) : (
              e.label
            )}
            {e.draft && <span className="text-faint"> (draft)</span>}
            {e.late && <span className="text-danger"> · past due</span>}
            {e.lost ? (
              <span className="text-danger" title="Forecast sales there's no stock for: lost, as the plan counts them">
                {" "}
                · {fmt(Math.round(e.lost))} short
              </span>
            ) : null}
          </span>
        ),
      },
      {
        key: "qty",
        header: "In / out",
        width: "72px",
        align: "right",
        value: (e) => (e.kind === "start" ? null : Math.round(e.qty)),
        cell: (e) => <span className={cx("font-mono", e.qty > 0 && "text-ok", e.kind === "sales" && e.qty <= 0 && "text-muted")}>{change(e)}</span>,
      },
      {
        key: "balance",
        header: "Available",
        width: "80px",
        align: "right",
        value: (e) => Math.round(e.balance),
        cell: (e) => (
          <span className={cx("font-mono font-medium", e.balance < 0 ? "text-danger" : e.balance < floor ? "text-warn" : e.kind === "sales" && "text-muted")}>
            {fmt(Math.round(e.balance))}
          </span>
        ),
      },
    ],
    [today, floor],
  );
  return (
    <section className="mt-1 border-t border-line px-2 pt-2 pb-2">
      {/* Title, the floor and the table's menus on one line; the headline under it (a narrow pane has no room for all four) */}
      <div className="flex items-center gap-2 pb-0.5">
        <h3 className="shrink-0 text-[11px] font-medium tracking-wide text-faint uppercase">Projected stock</h3>
        <span className="min-w-0 truncate text-[11px] text-faint">
          {floor > 0 ? `against ${r.stockPolicy === "min_max" ? "the min" : "safety stock"} of ${fmt(Math.ceil(floor))}` : "no safety stock"}
        </span>
      </div>
      <span className={cx("block pb-1 text-[12px]", out !== null ? "text-danger" : below !== null ? "text-warn" : "text-ok")}>
        {out !== null
          ? out <= 0
            ? "Out now"
            : `Runs out ${day(out)}`
          : below !== null
            ? `Below ${r.stockPolicy === "min_max" ? "min" : "safety stock"} ${day(below)}`
            : `Above ${r.stockPolicy === "min_max" ? "min" : "safety stock"} throughout`}
      </span>
      <DataTable fit rows={rows} columns={columns} defaultSort={{ key: "when" }} rowNoun={["row", "rows"]} exportName="Projected stock" pageFilters={false} />
    </section>
  );
}
